"use client"

import { useEffect } from "react"

/**
 * Browser-side PostHog, in one place — loaded when the page is idle.
 *
 * posthog-js is ~60 KiB gzipped and nothing on screen waits for it, so it no
 * longer rides the app shell (Tasks performance Tier 3.5). This module is a
 * thin facade: every call made before the library has loaded is queued and
 * replayed in order once it has. The load is scheduled from the first idle
 * frame after hydration (five seconds at the latest), not from the first call,
 * so an early `capture` does not pull the library onto the critical path.
 *
 * It lives outside the provider component on purpose. `app/global-error.tsx`
 * REPLACES the root layout when it fires, so no provider is mounted — the one
 * moment you most want an error reported is the one moment React context
 * isn't there. Importing the facade from here works from any client module.
 *
 * Consequently: report errors through `useReportError` / the `posthog` export
 * here, never through posthog-js/react.
 */

type Client = typeof import("posthog-js").default
type Props = Record<string, unknown>

const KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY

let client: Client | null = null
let loading: Promise<Client | null> | null = null
const queue: Array<(ph: Client) => void> = []

// Until the library installs its own handlers, remember what it would have
// caught. Removed once it is up so nothing is reported twice.
const earlyError = (e: ErrorEvent) => run((ph) => ph.captureException(e.error ?? e.message))
const earlyRejection = (e: PromiseRejectionEvent) => run((ph) => ph.captureException(e.reason))

function load(): Promise<Client | null> {
  if (!KEY || typeof window === "undefined") return Promise.resolve(null)
  if (!loading) {
    loading = import("posthog-js")
      .then(({ default: ph }) => {
        ph.init(KEY, {
          api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
          capture_pageview: false, // we capture manually for SPA navigation
          capture_pageleave: true,
          autocapture: false, // we only want performance data
          // Unhandled errors and unhandled promise rejections, reported without
          // any per-call-site code. Boundaries below add the ones React swallows.
          capture_exceptions: true,
        })
        window.removeEventListener("error", earlyError)
        window.removeEventListener("unhandledrejection", earlyRejection)
        client = ph
        for (const fn of queue.splice(0)) fn(ph)
        return ph
      })
      .catch(() => null)
  }
  return loading
}

function run(fn: (ph: Client) => void): void {
  if (client) fn(client)
  else if (KEY && typeof window !== "undefined") queue.push(fn)
}

if (typeof window !== "undefined") {
  if (KEY) {
    window.addEventListener("error", earlyError)
    window.addEventListener("unhandledrejection", earlyRejection)
    const idle = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 2000))
    idle(() => void load(), { timeout: 5000 })
  } else if (process.env.NODE_ENV !== "production") {
    // Never break the app over a missing key — but never be quiet about it
    // either, or you find out months later that nothing was ever reported.
    console.error(
      "[posthog] NEXT_PUBLIC_POSTHOG_KEY variable required by PostHog is missing or un-configured, " +
        "this causes events to be silently missed. This error stops appearing once " +
        "NEXT_PUBLIC_POSTHOG_KEY is configured",
    )
  }
}

/** The three calls the app makes. Same shape as posthog-js, queued until it loads. */
export const posthog = {
  capture(event: string, properties?: Props) {
    run((ph) => ph.capture(event, properties))
  },
  captureException(error: unknown, properties?: Props) {
    run((ph) => ph.captureException(error, properties))
  },
  identify(distinctId: string, properties?: Props) {
    run((ph) => ph.identify(distinctId, properties))
  },
}

/**
 * Report an error caught by a React error boundary.
 *
 * React error boundaries (`error.tsx`) CATCH the error, which means the global
 * `window.onerror` handler that `capture_exceptions` installs never sees it.
 * Without this call, every crash that renders a "Something went wrong" screen
 * is invisible in Error Tracking — exactly the errors users actually hit.
 *
 * `digest` is the id Next prints on the fallback screen and stamps on the
 * matching server-side event, so it's the join key between what the user
 * reports ("it said Ref: 3f2a…") and the server exception.
 */
export function useReportError(error: (Error & { digest?: string }) | undefined): void {
  useEffect(() => {
    if (!error) return
    posthog.captureException(error, error.digest ? { digest: error.digest } : undefined)
  }, [error])
}
